/**
 * MUI theme factory.
 *
 * Creates a theme based on language. Persian uses Vazirmatn font and
 * RTL direction; English uses Roboto and LTR.
 *
 * Customize colors, typography, and component defaults here.
 */

import { createTheme } from '@mui/material/styles';

/**
 * Build a Material-UI theme for the given language.
 * @param {'fa'|'en'} language
 */
export function buildTheme(language) {
  const isRtl = language === 'fa';

  return createTheme({
    direction: isRtl ? 'rtl' : 'ltr',
    palette: {
      mode: 'light',
      primary: { main: '#11223E', light: '#3B4D72', dark: '#0A1425', contrastText: '#fff' },
      // Antique brass / gold accent — the classic partner to deep navy, and a
      // refined warm note against the sand canvas.
      secondary: { main: '#8C6D1F', light: '#B7994A', dark: '#6A5115', contrastText: '#fff' },
      success: { main: '#2E7D55', contrastText: '#fff' },
      error: { main: '#C0433A', contrastText: '#fff' },
      warning: { main: '#C0872E' },
      info: { main: '#4A7290', contrastText: '#fff' },
      background: {
        default: '#C0A786',
        paper: '#ffffff',
      },
    },
    typography: {
      fontFamily: isRtl
        ? '"Vazirmatn Variable", "Segoe UI", Tahoma, sans-serif'
        : '"Plus Jakarta Sans Variable", "Vazirmatn Variable", "Segoe UI", Roboto, sans-serif',
      fontSize: 14,
    },
    shape: {
      borderRadius: 14,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Modern, minimal scrollbar that matches the theme: a thin, rounded,
          // "floating" translucent-navy thumb on a transparent track.
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(17, 34, 62, 0.35)', // brand navy #11223E @ 35%
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(17, 34, 62, 0.6)' },
          '*::-webkit-scrollbar-thumb:active': { backgroundColor: 'rgba(17, 34, 62, 0.75)' },
          '*::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
          html: { scrollbarWidth: 'thin', scrollbarColor: 'rgba(17, 34, 62, 0.45) transparent' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
          // The brand navy is ideal for headers/text but reads almost black as a
          // filled button. Use a lighter, livelier royal blue for contained CTAs.
          containedPrimary: {
            backgroundColor: '#2C4A78',
            '&:hover': { backgroundColor: '#223A61' },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', fullWidth: true },
      },
    },
  });
}
